class StorePicker extends HTMLElement {
    constructor() {
        super();
        this.onBodyClick = this.handleBodyClick.bind(this);
        this.onKeyup = this.handleKeyup.bind(this);
        this.elements = {
            selected: this.querySelector('strong'),
            details: this.querySelector('details'),
            summary: this.querySelector('summary')
        };
        // Store locations will be populated from the global storeLocations array
        this.storeLocations = [];
        // Added property to store original order
        this.originalStoreOrder = [];
        // Track current search location (initially check localStorage)
        this.currentSearchLocation = localStorage.getItem('userSearchLocation') || '';
        // Track if geolocation is in progress
        this.geoLocationInProgress = false;
        // Add flag to track if this is an initial load/auto request
        this.isInitialLoad = true;

        document.body.addEventListener('click', this.onBodyClick);

        console.log('Store Picker initialized');
        // Add location features outside the dropdown
        this.addExternalLocationFeatures();

        // Then try to initialize with store locator data
        this.initWithStoreLocatorData();
    }
    
    connectedCallback() {
        if (!this.hasAttribute('user-store') && localStorage.getItem('userStoreId')) {
            this.userStore = localStorage.getItem('userStoreId');
            console.log('Connected with stored userStore:', this.userStore);
        }

        // If we have a saved location, load it
        this.loadSavedLocation();
        
        // After a brief delay to ensure the DOM is ready, request geolocation if needed
        setTimeout(() => {
            this.autoRequestGeolocation();
            // Move buttons below addresses
            this.moveButtonsBelowAddress();
            
            // After initialization is complete, set initialLoad to false
            setTimeout(() => {
                this.isInitialLoad = false;
            }, 1000);
        }, 500);
    }
    
    /**
     * Moves all store selection buttons below the address for each store
     */
    moveButtonsBelowAddress() {
        // Find all store items in the list
        const storeItems = this.querySelectorAll('li.store-picker__store');
        
        storeItems.forEach(item => {
            const buttonElement = item.querySelector('.store-fav-selector');
            const addressElement = item.querySelector('.store-picker__store-address');
             
            // Only proceed if both elements exist
            if (buttonElement && addressElement) {
                // Remove the button from its current location
                buttonElement.parentNode.removeChild(buttonElement);
                
                // Add the button after the address element
                addressElement.insertAdjacentElement('afterend', buttonElement);
                
            }
        });
    }

    // Auto-request geolocation on page load if needed
    autoRequestGeolocation() {
        // Only auto-request if:
        // 1. We don't have a saved location
        // 2. Geolocation is supported
        // 3. We're not already in progress with a geolocation request
        const savedLocation = localStorage.getItem('userSearchLocation');
        const savedCoordinates = localStorage.getItem('userSearchCoordinates');
        
        if (!savedLocation && !this.geoLocationInProgress && navigator.geolocation) {
            console.log('Auto-requesting geolocation permission...');
            this.getUserLocation(true); // true indicates this is an auto-request
        } 
        // If we have saved coordinates but no search performed yet, use those
        else if (savedCoordinates && !this.locationSearchPerformed) {
            try {
                const coords = JSON.parse(savedCoordinates);
                if (coords && coords.latitude && coords.longitude) {
                    console.log('Using saved coordinates for initial search', coords);
                    this.findNearestStore(coords.latitude, coords.longitude, true); // true = auto request
                    this.locationSearchPerformed = true;
                }
            } catch (e) {
                console.error('Error parsing saved coordinates', e);
            }
        }
    }

    // Load saved location and perform search if needed
    loadSavedLocation() {
        const savedLocation = localStorage.getItem('userSearchLocation');
        const savedLocationType = localStorage.getItem('userSearchLocationType');
        const savedCoordinates = localStorage.getItem('userSearchCoordinates');
        
        if (savedLocation) {
            console.log(`Found saved location: ${savedLocation}, type: ${savedLocationType}`);
            this.currentSearchLocation = savedLocation;
            this.updateLocationDisplay();
            this.locationSearchPerformed = false;
            
            // If the saved location is a ZIP code, perform a search with it
            if (savedLocationType === 'zip' && /^\d{5}$/.test(savedLocation)) {
                // Wait a bit for the page to load and then perform search
                setTimeout(() => {
                    console.log('Performing search with saved ZIP:', savedLocation);
                    this.getZipCodeCoordinates(savedLocation)
                        .then(coords => {
                            if (coords) {
                                this.findNearestStore(coords.latitude, coords.longitude, true); // true = auto request
                                this.locationSearchPerformed = true;
                            }
                        })
                        .catch(error => {
                            console.error('Error searching with saved ZIP:', error);
                        });
                }, 500);
            }
            // If we have saved coordinates, use those instead of requesting geolocation again
            else if (savedLocationType === 'geo' && savedCoordinates) {
                try {
                    const coords = JSON.parse(savedCoordinates);
                    if (coords && coords.latitude && coords.longitude) {
                        setTimeout(() => {
                            console.log('Using saved coordinates', coords);
                            this.findNearestStore(coords.latitude, coords.longitude, true); // true = auto request
                            this.locationSearchPerformed = true;
                        }, 500);
                    }
                } catch (e) {
                    console.error('Error parsing saved coordinates', e);
                }
            }
        }
    }

    // Set the current location and save it to localStorage
    setCurrentLocation(location, type = 'zip', coordinates = null) {
        this.currentSearchLocation = location;
        localStorage.setItem('userSearchLocation', location);
        localStorage.setItem('userSearchLocationType', type);
        
        // If coordinates are provided, save them too
        if (coordinates) {
            localStorage.setItem('userSearchCoordinates', JSON.stringify(coordinates));
        }
        
        this.updateLocationDisplay();
        
        // Dispatch event to notify other components
        document.dispatchEvent(new CustomEvent('locationChanged', {
            detail: {
                location: location,
                type: type,
                coordinates: coordinates
            }
        }));
    }

    // Clear the saved location
    clearSavedLocation() {
        this.currentSearchLocation = '';
        localStorage.removeItem('userSearchLocation');
        localStorage.removeItem('userSearchLocationType');
        localStorage.removeItem('userSearchCoordinates');
        this.updateLocationDisplay();
        
        // Notify other components
        document.dispatchEvent(new CustomEvent('locationChanged', {
            detail: {
                location: null,
                type: null,
                coordinates: null
            }
        }));
    }

    // Initialize with data from store locator
    initWithStoreLocatorData() {
        console.log('Looking for store locator data...');

        // Check if global storeLocations array exists (from store-locator.js)
        if (typeof window.storeLocations !== 'undefined' && window.storeLocations.length > 0) {
            console.log(`Found ${window.storeLocations.length} stores in global storeLocations array`);
            this.processStoreLocatorData();
        } else {
            // Try to find the storeLocations variable in any scope
            console.log('Global storeLocations not found, trying alternate methods...');

            // Check if there's a variable called storeLocations in the current scope
            if (typeof storeLocations !== 'undefined') {
                window.storeLocations = storeLocations;
                console.log('Found storeLocations in current scope, processing...');
                this.processStoreLocatorData();
            } else {
                // As a last resort, try to extract store data from the page
                console.log('No storeLocations array found. Will use basic functionality.');

                // Attempt to use store picker data only
                this.extractStoreDataFromPicker();
            }
        }
    }

    // Process store locator data and match with store picker items
    processStoreLocatorData() {
        // Get all store items in the picker
        const storeItems = this.querySelectorAll('li[data-store-id]');
        console.log(`Found ${storeItems.length} store items in picker`);

        if (storeItems.length === 0) {
            console.log('No store items found in picker, using locator data directly');

            // If no store items in picker, use locator data directly
            window.storeLocations.forEach(store => {
                if (store.coordinates && !store.deliveryOnly) {
                    this.storeLocations.push({
                        id: store.id,
                        name: store.name,
                        address: store.address || '',
                        city: store.city || '',
                        state: store.state || '',
                        zip: store.zip || '',
                        coordinates: {
                            lat: store.coordinates.lat,
                            lng: store.coordinates.lng
                        }
                    });
                }
            });
        } else {
            // Map store locator data to store picker items
            storeItems.forEach(item => {
                const storeId = item.dataset.storeId;
                const storeName = item.dataset.storeName;

                // Try to find a matching store in the global storeLocations array
                let matchedStore = this.findMatchingStore(storeId, storeName);

                if (matchedStore) {
                    console.log(`Matched store "${storeName}" with store locator data`);

                    // Store the coordinates and other relevant info
                    item.dataset.latitude = matchedStore.coordinates.lat;
                    item.dataset.longitude = matchedStore.coordinates.lng;

                    // Add to our internal store locations array
                    this.storeLocations.push({
                        id: storeId,
                        name: storeName,
                        address: matchedStore.address || '',
                        city: matchedStore.city || '',
                        state: matchedStore.state || '',
                        zip: matchedStore.zip || '',
                        coordinates: {
                            lat: matchedStore.coordinates.lat,
                            lng: matchedStore.coordinates.lng
                        }
                    });
                } else {
                    console.log(`No match found for store "${storeName}" in store locator data`);
                }
            });
        }

        console.log(`Processed ${this.storeLocations.length} stores with coordinates`);
    }

    // Extract store data from picker without store locator
    extractStoreDataFromPicker() {
        const storeItems = this.querySelectorAll('li[data-store-id]');
        console.log(`Found ${storeItems.length} store items in picker`);

        storeItems.forEach(item => {
            const storeId = item.dataset.storeId;
            const storeName = item.dataset.storeName;

            // Check if the item already has coordinates
            if (item.dataset.latitude && item.dataset.longitude) {
                this.storeLocations.push({
                    id: storeId,
                    name: storeName,
                    coordinates: {
                        lat: parseFloat(item.dataset.latitude),
                        lng: parseFloat(item.dataset.longitude)
                    }
                });
            }
        });
    }
    // Find a matching store from the global storeLocations array
    // Find a matching store from the global storeLocations array
    findMatchingStore(storeId, storeName) {
        // Helper function to convert "Super-Sod of Birmingham" to "super-sod-birmingham" format
        const convertPickerNameToLocatorId = (name) => {
            // Remove "of" and convert to lowercase with hyphens
            return name.toLowerCase()
                .replace(/\s+of\s+/g, '-')  // Replace " of " with "-"
                .replace(/[^a-z0-9-]/g, '')   // Remove non-alphanumeric except hyphens
                .replace(/--+/g, '-')         // Replace multiple hyphens with single hyphen
                .replace(/^-|-$/g, '');       // Remove leading/trailing hyphens
        };

        // First try direct ID match
        let matchedStore = window.storeLocations.find(store => {
            return store.id === storeId;
        });

        // If no match by ID, try converting picker name to locator ID format
        if (!matchedStore) {
            const convertedId = convertPickerNameToLocatorId(storeName);
            console.log(`Trying to match "${storeName}" as "${convertedId}"`);

            matchedStore = window.storeLocations.find(store => {
                return store.id === convertedId;
            });
        }

        // If no match by converted ID, try normalized name matching
        if (!matchedStore) {
            const normalizeName = name => name.toLowerCase().replace(/[^a-z0-9]/g, '');
            const normalizedStoreName = normalizeName(storeName);

            matchedStore = window.storeLocations.find(store => {
                return normalizeName(store.name) === normalizedStoreName;
            });
        }

        // If still no match, try extracting city name from picker name and matching
        if (!matchedStore) {
            // Extract city from "Super-Sod of CityName" format
            const cityMatch = storeName.match(/Super-Sod\s+of\s+(.+)/i);
            if (cityMatch) {
                const cityName = cityMatch[1].trim();

                matchedStore = window.storeLocations.find(store => {
                    // Check if store ID contains the city name
                    const normalizedCity = cityName.toLowerCase().replace(/[^a-z0-9]/g, '');
                    const normalizedStoreId = store.id.toLowerCase().replace(/[^a-z0-9]/g, '');
                    return normalizedStoreId.includes(normalizedCity) ||
                        (store.city && store.city.toLowerCase().replace(/[^a-z0-9]/g, '') === normalizedCity);
                });

                if (matchedStore) {
                    console.log(`Found match for "${storeName}" by city name "${cityName}": "${matchedStore.name}"`);
                }
            }
        }

        // Special case handling for specific mismatches
        if (!matchedStore) {
            // Map of special cases: picker name -> locator id
            const specialCases = {
                'Super-Sod of Hendersonville': 'super-sod-hendersonville',
                'Super-Sod of Birmingham': 'super-sod-birmingham',
                'Super-Sod of Manchester': 'super-sod-manchester',
                'Super-Sod of Opelika': 'super-sod-opelika',
                'Super-Sod of Cartersville': 'super-sod-cartersville',
                'Super-Sod of Benton': 'super-sod-benton',
                'Super-Sod of Newnan': 'super-sod-newnan',
                'Super-Sod of Marietta': 'super-sod-marietta',
                'Super-Sod of Canton': 'super-sod-canton',
                'Super-Sod of Knoxville': 'super-sod-knoxville',
                'Super-Sod of Forest Park': 'super-sod-forest-park',
                'Super-Sod of Alpharetta': 'super-sod-alpharetta',
                'Super-Sod of Dawsonville': 'super-sod-dawsonville',
                'Super-Sod of Mooresville': 'super-sod-mooresville',
                'Super-Sod of Orangeburg': 'super-sod-orangeburg',
                'Super-Sod of Cary': 'super-sod-cary',
                'Super-Sod of Raleigh': 'super-sod-raleigh',
                'Super-Sod of Middle Georgia': 'super-sod-middle-georgia',
                'Super-Sod of Nashville': 'super-sod-nashville',
                'Super-Sod of Gwinnett': 'super-sod-gwinnett',
                'Super-Sod of Charleston': 'super-sod-charleston',
                'Super-Sod of Charlotte': 'super-sod-charlotte',
                'Super-Sod of Greenville': 'super-sod-greenville',
                'Super-Sod of Columbia': 'super-sod-columbia'
            };

            if (specialCases[storeName]) {
                matchedStore = window.storeLocations.find(store => {
                    return store.id === specialCases[storeName];
                });

                if (matchedStore) {
                    console.log(`Found match for "${storeName}" using special case mapping`);
                }
            }
        }

        // Final fallback: partial name match
        if (!matchedStore) {
            const partialNameMatch = window.storeLocations.find(store => {
                return storeName.toLowerCase().includes(store.city.toLowerCase()) ||
                    store.name.toLowerCase().includes(storeName.toLowerCase());
            });

            if (partialNameMatch) {
                console.log(`Found partial name match for "${storeName}": "${partialNameMatch.name}"`);
                matchedStore = partialNameMatch;
            }
        }

        return matchedStore;
    }


    // Add location features outside the dropdown, next to location-settings__content
    addExternalLocationFeatures() {
        console.log('Adding external location features');

        // Find the location-settings__content parent
        const storePicker = this;
        const parentElement = this.closest('.location-settings__content');

        if (!parentElement) {
            console.error('Could not find location-settings__content parent');
            return;
        }

        // Create location features container
        const locationFeatures = document.createElement('div');
        locationFeatures.className = 'location-finder';

        locationFeatures.innerHTML = `
            <div class="location-finder__body">
                <div class="location-finder__buttons">
                    <button type="button" class="use-my-location-button" style="width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 8px 10px; font-size: 12px;">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <circle cx="12" cy="12" r="1"></circle>
                            <line x1="12" y1="2" x2="12" y2="4"></line>
                            <line x1="12" y1="20" x2="12" y2="22"></line>
                            <line x1="2" y1="12" x2="4" y2="12"></line>
                            <line x1="20" y1="12" x2="22" y2="12"></line>
                        </svg>
                        Use My Location
                    </button>
                </div>
                <div class="location-finder__zip-search">
                    <div class="zip-search-form" style="display: flex; gap: 5px;">
                        <div class="field" style="flex-grow: 1;">
                            <input type="text" 
                                  id="zipCodeSearch" 
                                  class="field__input" 
                                  placeholder="Enter ZIP Code" 
                                  pattern="[0-9]{5}" 
                                  maxlength="5"
                                  >
                        </div>
                        <button type="button" class="btn btn--secondary zip-search-button" style="white-space: nowrap; padding: 8px; font-size: 12px;">
                            Set Store
                        </button>
                    </div>
                </div>
            </div>
        `;

        // Insert after the store-picker
        parentElement.appendChild(locationFeatures);

        // Add error message container
        const errorContainer = document.createElement('div');
        errorContainer.className = 'location-error';
        locationFeatures.appendChild(errorContainer);

        // Store direct references to the elements we'll need later
        let zipInputElement, zipSearchButtonElement;

        // Small delay to ensure DOM is updated before we try to find elements
        setTimeout(() => {
            // Get element references after DOM has been updated
            zipInputElement = locationFeatures.querySelector('#zipCodeSearch');
            zipSearchButtonElement = locationFeatures.querySelector('.zip-search-button');
            const useLocationButton = locationFeatures.querySelector('.use-my-location-button');

            // Add event listeners for "use my location" button
            if (useLocationButton) {
                useLocationButton.addEventListener('click', () => {
                    // Open the dropdown first for better UX
                    this.open = true;
                    // Then use location
                    this.getUserLocation(false); // false indicates this is a manual request
                });
            }

            // Add event listener for ZIP search button
            if (zipSearchButtonElement) {
                console.log('Adding click listener to ZIP search button');
                zipSearchButtonElement.addEventListener('click', () => {
                    console.log('ZIP search button clicked');
                    if (zipInputElement) {
                        console.log('ZIP input found, value:', zipInputElement.value);
                        // Open the dropdown first for better UX
                        this.open = true;
                        // Then search by zip, passing the value directly
                        this.searchByZipCode(zipInputElement.value);
                    } else {
                        console.error('ZIP input field not found on button click');
                    }
                });
            } else {
                console.error('ZIP search button not found');
            }

            // Add event listener for ZIP input field
            if (zipInputElement) {
                console.log('Adding keypress listener to ZIP input field');
                zipInputElement.addEventListener('keypress', (e) => {
                    if (e.key === 'Enter') {
                        console.log('Enter key pressed in ZIP input, value:', zipInputElement.value);
                        // Open the dropdown first for better UX
                        this.open = true;
                        // Then search by zip, passing the value directly
                        this.searchByZipCode(zipInputElement.value);
                    }
                });

                // Set zip input value if we have a saved ZIP
                const savedLocation = localStorage.getItem('userSearchLocation');
                const savedLocationType = localStorage.getItem('userSearchLocationType');
                
                if (savedLocation && savedLocationType === 'zip' && /^\d{5}$/.test(savedLocation)) {
                    zipInputElement.value = savedLocation;
                }
            } else {
                console.error('ZIP input field not found during event setup');
            }
        }, 100);

        console.log('External location features added successfully');
        
        // Create the location display div
        this.createLocationDisplayElement();
    }
    
    // Method to create or update the location display element
    createLocationDisplayElement() {
        const parentElement = this.closest('.location-settings');
        if (!parentElement) return;
        
        // Check if location display already exists
        let locationDisplay = parentElement.querySelector('.store-location-display');
        
        // If it doesn't exist, create it
        if (!locationDisplay) {
            locationDisplay = document.createElement('div');
            locationDisplay.className = 'store-location-display';
            locationDisplay.style.cssText = 'margin-top: 5px; margin-left: 0; font-size: 12px; color: #666; display: none;';
            
            // Insert it after the summary element
            const summaryElement = this.querySelector('summary');
            if (summaryElement) {
                const summaryParent = summaryElement.parentElement;
                if (summaryParent) {
                    summaryParent.insertAdjacentElement('afterend', locationDisplay);
                } else {
                    parentElement.appendChild(locationDisplay);
                }
            } else {
                parentElement.appendChild(locationDisplay);
            }
        }
        
        // Update the initial display if we have a saved location
        this.updateLocationDisplay();
    }
    
    // Method to update the location display
    updateLocationDisplay() {
        const locationDisplay = document.querySelector('.store-location-display');
        if (!locationDisplay) return;
        
        if (this.currentSearchLocation) {
            locationDisplay.textContent = `Showing stores near ${this.currentSearchLocation}`;
            locationDisplay.style.display = 'none';
        } else {
            locationDisplay.style.display = 'none';
        }
    }
    
    // Add zip search results container to the dropdown
    addDropdownLocationResults() {
        // Simplified - we'll use the existing store list
        console.log('Using existing store list for location results');
    }
    
    /**
     * Get user location with proper IP fallback
     * @param {boolean} isAutoRequest - Whether this is an automatic request on page load
     */
    getUserLocation(isAutoRequest = false) {
        console.log('[LOCATION] getUserLocation called, autoRequest:', isAutoRequest);
        
        // Set flag to prevent multiple geolocation requests
        if (this.geoLocationInProgress) {
            console.log('[LOCATION] Geolocation already in progress, ignoring request');
            return;
        }
        
        this.geoLocationInProgress = true;
        
        // Only show loading and update display for manual requests
        if (!isAutoRequest) {
            // Set location indicator
            this.setCurrentLocation('your current location', 'geo');

            // Show loading state
            const useLocationButton = document.querySelector('.use-my-location-button');
            if (useLocationButton) {
                useLocationButton.innerHTML = '<span class="loading-spinner" style="display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(0,0,0,0.1); border-left-color: currentColor; border-radius: 50%; animation: spinner 0.75s linear infinite;"></span> Locating...';
                useLocationButton.disabled = true;
                console.log('[LOCATION] Updated button to loading state');
            }

            // Open the dropdown for manual requests
            this.open = true;
        }
        
        console.log('[LOCATION] Opened dropdown');

        // Check if browser supports geolocation
        if (!navigator.geolocation) {
            console.error('[LOCATION] Browser does not support geolocation');
            this.geoLocationInProgress = false;
            // Call IP-based fallback instead of giving up
            this.fallbackToIpGeolocation(document.querySelector('.use-my-location-button'), isAutoRequest);
            return;
        }

        console.log('[LOCATION] Browser supports geolocation, requesting position...');

        // Attempt to get user location with simplified options
        navigator.geolocation.getCurrentPosition(
            // Success callback
            (position) => {
                console.log('[LOCATION] Successfully got position:', position);
                const userLat = position.coords.latitude;
                const userLng = position.coords.longitude;

                console.log(`[LOCATION] Coordinates: ${userLat}, ${userLng}`);
                
                // Save the coordinates for future use
                this.setCurrentLocation('your current location', 'geo', {
                    latitude: userLat,
                    longitude: userLng
                });

                // Try to find nearest store with these coordinates
                this.findNearestStore(userLat, userLng, isAutoRequest);

                // Reset button for manual requests
                if (!isAutoRequest) {
                    this.resetLocationButton(document.querySelector('.use-my-location-button'));
                }
                
                // Reset flag
                this.geoLocationInProgress = false;

                // Ensure dropdown stays open for manual requests
                if (!isAutoRequest) {
                    this.open = true;
                }
                
                // Also notify the Store Locator component if it exists
                if (typeof userLocation !== 'undefined' && typeof loadStores === 'function') {
                    userLocation = {
                        lat: userLat,
                        lng: userLng
                    };
                    loadStores();
                }
            },
            // Error callback
            (error) => {
                console.error('[LOCATION] Geolocation error:', error);
                this.geoLocationInProgress = false;

                // Use IP-based fallback instead of hardcoded coordinates
                this.fallbackToIpGeolocation(document.querySelector('.use-my-location-button'), isAutoRequest);

                // Ensure dropdown stays open for manual requests
                if (!isAutoRequest) {
                    this.open = true;
                }
            },
            // Options
            { 
                timeout: 10000,
                maximumAge: isAutoRequest ? 0 : 600000 // Use fresh location for auto-requests
            }
        );
    }
    
    /**
     * Improved IP-based fallback method with better error handling
     */
    fallbackToIpGeolocation(button, isAutoRequest = false) {
        console.log("[IP LOCATION] Attempting IP-based geolocation...");

        // Force a fresh request by adding a cache-busting parameter
        const timestamp = new Date().getTime();
        const ipInfoUrl = `https://ipinfo.io/json?_=${timestamp}`;

        fetch(ipInfoUrl, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Cache-Control': 'no-cache'
            },
            cache: 'no-store'
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`IP geolocation request failed: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                console.log("[IP LOCATION] IP geolocation data received:", data);

                // Extract location data
                if (data.loc) {
                    const [latitude, longitude] = data.loc.split(',').map(coord => parseFloat(coord));
                    console.log(`[IP LOCATION] IP-based coordinates: ${latitude}, ${longitude}`);
                    
                    // Update location if we have postal/zip code, otherwise use city
                    let displayLocation = 'your approximate location';
                    if (data.postal) {
                        displayLocation = data.postal;
                    } else if (data.city) {
                        displayLocation = data.city;
                    }
                    
                    // Save the location and coordinates
                    this.setCurrentLocation(displayLocation, 'geo', {
                        latitude: latitude,
                        longitude: longitude
                    });

                    // Find nearest store using coordinates
                    this.findNearestStore(latitude, longitude, isAutoRequest);

                    // Ensure dropdown stays open for manual requests
                    if (!isAutoRequest) {
                        this.open = true;
                    }
                    
                    // Also notify the Store Locator component if it exists
                    if (typeof userLocation !== 'undefined' && typeof loadStores === 'function') {
                        userLocation = {
                            lat: latitude,
                            lng: longitude
                        };
                        loadStores();
                    }
                } else {
                    throw new Error('Location data not available from IP geolocation');
                }
            })
            .catch(error => {
                console.error("[IP LOCATION] IP geolocation failed:", error);

                // Only show errors for manual requests
                if (!isAutoRequest) {
                    // Focus on the ZIP code input as fallback
                    const zipInput = document.getElementById('zipCodeSearch');
                    if (zipInput) {
                        zipInput.focus();
                    }

                    this.showLocationError("We couldn't determine your location. Please try entering your ZIP code instead.");
                }
            })
            .finally(() => {
                // Reset button for manual requests
                if (!isAutoRequest) {
                    this.resetLocationButton(button);
                }
                
                // Reset flag
                this.geoLocationInProgress = false;
            });
    }
    
    // Updated resetDisplay method
    resetDisplay() {
        // Reset search location
        this.clearSavedLocation();
        
        // Reset store view
        this.resetStoreView();
    }
    
    /**
     * Helper to reset location button
     */
    resetLocationButton(button) {
        if (button) {
            button.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <circle cx="12" cy="12" r="1"></circle>
                    <line x1="12" y1="2" x2="12" y2="4"></line>
                    <line x1="12" y1="20" x2="12" y2="22"></line>
                    <line x1="2" y1="12" x2="4" y2="12"></line>
                    <line x1="20" y1="12" x2="22" y2="12"></line>
                </svg>
                Use My Location
            `;
            button.disabled = false;
        }
    }

    /**
     * Find nearest store and handle results
     * @param {number} userLat - User latitude
     * @param {number} userLng - User longitude
     * @param {boolean} isAutoRequest - Whether this is from an automatic request
     */
    findNearestStore(userLat, userLng, isAutoRequest = false) {
        console.log(`Finding stores near coordinates: ${userLat}, ${userLng}, autoRequest: ${isAutoRequest}`);

        // Validate inputs
        if (typeof userLat !== 'number' || typeof userLng !== 'number' || isNaN(userLat) || isNaN(userLng)) {
            console.error('Invalid coordinates:', userLat, userLng);
            this.showLocationError("Invalid location coordinates received.");
            return;
        }

        // Validate store data
        if (!this.storeLocations || this.storeLocations.length === 0) {
            console.error('No store locations available for distance calculation');
            
            // Try to use window.storeLocations directly if available
            if (typeof window.storeLocations !== 'undefined' && window.storeLocations.length > 0) {
                console.log('Using global storeLocations array');
                this.storeLocations = window.storeLocations.map(store => ({
                    id: store.id,
                    name: store.name,
                    address: store.address || '',
                    city: store.city || '',
                    state: store.state || '',
                    zip: store.zip || '',
                    coordinates: store.coordinates
                }));
            } else {
                this.showLocationError("Store location data is not available.");
                return;
            }
        }

        console.log(`Calculating distances for ${this.storeLocations.length} stores...`);

        // Calculate distance to each store
        const storesWithDistance = this.storeLocations.map(store => {
            // Validate store coordinates
            if (!store.coordinates ||
                typeof store.coordinates.lat !== 'number' ||
                typeof store.coordinates.lng !== 'number' ||
                isNaN(store.coordinates.lat) ||
                isNaN(store.coordinates.lng)) {
                console.warn(`Store ${store.name} has invalid coordinates:`, store.coordinates);
                return { ...store, distance: Infinity }; // Use Infinity to sort this to the end
            }

            // Use window.calculateDistance if available (from store-locator.js)
            let distance;
            if (typeof window.calculateDistance === 'function') {
                distance = window.calculateDistance(
                    userLat, userLng,
                    parseFloat(store.coordinates.lat),
                    parseFloat(store.coordinates.lng)
                );
            } else {
                distance = this.calculateDistance(
                    userLat, userLng,
                    parseFloat(store.coordinates.lat),
                    parseFloat(store.coordinates.lng)
                );
            }

            return {
                ...store,
                distance: distance
            };
        });

        // Filter out stores with invalid distances
        const validStores = storesWithDistance.filter(store => store.distance !== Infinity);

        if (validStores.length === 0) {
            console.error('No stores with valid distances found');
            this.showLocationError("We couldn't find any stores near your location.");
            return;
        }

        // Sort by distance
        validStores.sort((a, b) => a.distance - b.distance);

        console.log('Nearest stores:', validStores.slice(0, 3).map(s => `${s.name}: ${s.distance.toFixed(2)} miles`));

        // Display results
        this.displayNearbyStores(validStores);

        // Automatically select the nearest store
        if (validStores.length > 0) {
            const nearestStore = validStores[0];
            console.log(`Selecting nearest store: ${nearestStore.name} (${nearestStore.distance.toFixed(2)} miles away)`);
            
            // Only auto-select if this is a manual request or if no store is already selected
            if (!isAutoRequest || !this.userStore) {
                this.userStore = nearestStore.id;
                
                // Save to localStorage
                localStorage.setItem('userStoreId', nearestStore.id);
                localStorage.setItem('userStoreName', nearestStore.name);
                
                // Dispatch event to notify other components
                document.dispatchEvent(new CustomEvent('storeSelected', {
                    detail: {
                        storeId: nearestStore.id,
                        storeName: nearestStore.name
                    }
                }));
            }

            // Make sure the dropdown stays open to show results only if manual request
            if (!isAutoRequest && !this.isInitialLoad) {
                this.open = true;
            }
        }
    }
    
    // Fixed searchByZipCode method
    searchByZipCode(directValue) {
        console.log('searchByZipCode called, direct value:', directValue);
        
        // Use the direct value if provided
        let zipCode = '';
        
        if (directValue) {
            // Use the value passed directly from the event handler
            zipCode = directValue.replace(/\D/g, '').substring(0, 5);
            console.log('Using direct ZIP value:', zipCode);
        } else {
            console.error('No ZIP code value provided');
            this.showLocationError("Please enter a ZIP code");
            return;
        }
        
        console.log('Final sanitized ZIP code:', zipCode);
        
        // Validate zip code length after sanitization
        if (!zipCode || zipCode.length !== 5) {
            console.log(`ZIP code validation failed: "${zipCode}" is not 5 digits`);
            this.showLocationError("Please enter a valid 5-digit ZIP code");
            return;
        }
        
        console.log(`ZIP code "${zipCode}" passed validation`);
        
        // Save the zip code for display and persistence
        this.setCurrentLocation(zipCode, 'zip');

        const zipSearchButton = document.querySelector('.zip-search-button');
        if (zipSearchButton) {
            zipSearchButton.innerHTML = '<span class="loading-spinner" style="display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(0,0,0,0.1); border-left-color: currentColor; border-radius: 50%; animation: spinner 0.75s linear infinite;"></span> Searching...';
            zipSearchButton.disabled = true;
        }

        this.getZipCodeCoordinates(zipCode)
            .then(coords => {
                if (coords) {
                    console.log(`ZIP code ${zipCode} coordinates:`, coords);
                    
                    // Save the coordinates
                    this.setCurrentLocation(zipCode, 'zip', coords);
                    
                    // Mark this as a manual request (false = not auto)
                    this.findNearestStore(coords.latitude, coords.longitude, false);

                    // Ensure dropdown stays open to show results (this is a manual request)
                    this.open = true;
                    
                    // Notify the Store Locator component if it exists
                    if (typeof userLocation !== 'undefined' && typeof loadStores === 'function') {
                        userLocation = {
                            lat: coords.latitude,
                            lng: coords.longitude
                        };
                        loadStores({ isZipSearch: true });
                    }
                } else {
                    // Use default coordinates based on region if we can't get specific coordinates
                    this.useDefaultCoordinates(zipCode);
                }
            })
            .catch(error => {
                console.error('ZIP code search error:', error);
                // Use default coordinates as a fallback
                this.useDefaultCoordinates(zipCode);
            })
            .finally(() => {
                // Reset button
                if (zipSearchButton) {
                    zipSearchButton.innerHTML = 'Set Store';
                    zipSearchButton.disabled = false;
                }
            });
    }

    // Get coordinates for a ZIP code with improved error handling
    async getZipCodeCoordinates(zipCode) {
        try {
            // First, check if the Store Locator's geocoder is available
            if (typeof window.geocodeAddress === 'function') {
                return new Promise((resolve, reject) => {
                    window.geocodeAddress(zipCode, (location) => {
                        if (location) {
                            resolve({
                                latitude: location.lat,
                                longitude: location.lng
                            });
                        } else {
                            reject('Geocoding failed');
                        }
                    });
                });
            }
            
            // Next check if we have a store with this zip code
            if (window.storeLocations) {
                const storeWithZip = window.storeLocations.find(store => store.zip === zipCode);
                if (storeWithZip && storeWithZip.coordinates) {
                    console.log(`Found store with ZIP code ${zipCode}`);
                    return {
                        latitude: storeWithZip.coordinates.lat,
                        longitude: storeWithZip.coordinates.lng
                    };
                }

                // Also check if any stores have a similar ZIP code (first 3 digits)
                if (zipCode.length >= 3) {
                    const zipPrefix = zipCode.substring(0, 3);
                    const storeWithSimilarZip = window.storeLocations.find(store =>
                        store.zip && store.zip.startsWith(zipPrefix)
                    );

                    if (storeWithSimilarZip && storeWithSimilarZip.coordinates) {
                        console.log(`Found store with similar ZIP code ${storeWithSimilarZip.zip}`);
                        return {
                            latitude: storeWithSimilarZip.coordinates.lat,
                            longitude: storeWithSimilarZip.coordinates.lng
                        };
                    }
                }
            }

            // If no store with this ZIP, use a geocoding service
            console.log(`No store with ZIP code ${zipCode}, using geocoding service`);

            // Try with Google Maps Geocoder if available
            if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder) {
                console.log('Using Google Maps Geocoder');
                return new Promise((resolve, reject) => {
                    const geocoder = new google.maps.Geocoder();
                    geocoder.geocode({ 'address': zipCode + ', USA' }, function(results, status) {
                        if (status === google.maps.GeocoderStatus.OK && results[0]) {
                            resolve({
                                latitude: results[0].geometry.location.lat(),
                                longitude: results[0].geometry.location.lng()
                            });
                        } else {
                            reject(`Google geocoding failed: ${status}`);
                        }
                    });
                });
            }

            // Try with multiple APIs for reliability
            try {
                // First try zippopotam.us
                const timestamp = new Date().getTime();
                const response = await fetch(`https://api.zippopotam.us/us/${zipCode}?_=${timestamp}`, {
                    headers: {
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache'
                    },
                    cache: 'no-store'
                });

                if (!response.ok) {
                    throw new Error(`ZIP code lookup failed: ${response.status}`);
                }

                const data = await response.json();

                // Extract coordinates from the response
                if (data && data.places && data.places.length > 0) {
                    return {
                        latitude: parseFloat(data.places[0].latitude),
                        longitude: parseFloat(data.places[0].longitude)
                    };
                }
            } catch (error) {
                console.warn('Primary geocoding service failed, trying backup...', error);
                // We'll continue to the fallback approach
            }

            // If all APIs fail, use a static approach based on ZIP code regions
            return this.getZipCodeRegion(zipCode);
        } catch (error) {
            console.error('Error fetching ZIP code coordinates:', error);
            return this.getZipCodeRegion(zipCode);
        }
    }

    // Fallback function to get approximate coordinates based on ZIP code region
    getZipCodeRegion(zipCode) {
        // ZIP code regions (first 3 digits) with approximate coordinates
        const zipRegions = {
            // Northeast
            '010': { latitude: 42.1085, longitude: -72.5829 }, // Springfield, MA
            '100': { latitude: 40.7128, longitude: -74.0060 }, // New York, NY

            // South
            '300': { latitude: 33.7490, longitude: -84.3880 }, // Atlanta, GA
            '320': { latitude: 32.0809, longitude: -81.0912 }, // Savannah, GA
            '330': { latitude: 25.7617, longitude: -80.1918 }, // Miami, FL

            // Midwest
            '430': { latitude: 39.9612, longitude: -82.9988 }, // Columbus, OH
            '480': { latitude: 42.3314, longitude: -83.0458 }, // Detroit, MI

            // West
            '840': { latitude: 40.7608, longitude: -111.8910 }, // Salt Lake City, UT
            '900': { latitude: 34.0522, longitude: -118.2437 }, // Los Angeles, CA
            '940': { latitude: 37.7749, longitude: -122.4194 }, // San Francisco, CA
        };

        // Get first 3 digits of ZIP code
        const zipPrefix = zipCode.substring(0, 3);

        // Find the closest region
        let closestRegion = '100'; // Default to New York
        let smallestDifference = 999;

        for (const region in zipRegions) {
            const difference = Math.abs(parseInt(zipPrefix) - parseInt(region));
            if (difference < smallestDifference) {
                smallestDifference = difference;
                closestRegion = region;
            }
        }

        console.log(`Using approximate region for ZIP ${zipCode}: ${closestRegion}`);
        return zipRegions[closestRegion];
    }

    // Use default coordinates when all geocoding attempts fail
    useDefaultCoordinates(zipCode) {
        // Get approximate coordinates based on ZIP code region
        const coords = this.getZipCodeRegion(zipCode);

        if (coords) {
            console.log(`Using default coordinates for region near ${zipCode}:`, coords);
            
            // Save the coordinates
            this.setCurrentLocation(zipCode, 'zip', coords);
            
            // This is a manual request so pass false for isAutoRequest
            this.findNearestStore(coords.latitude, coords.longitude, false);
            
            // This is a manual request, so open the dropdown
            this.open = true;
            
            // Notify the Store Locator component if it exists
            if (typeof userLocation !== 'undefined' && typeof loadStores === 'function') {
                userLocation = {
                    lat: coords.latitude,
                    lng: coords.longitude
                };
                loadStores({ isZipSearch: true });
            }
        } else {
            // If we can't determine a region, just use a central US location
            console.log('Using central US coordinates as ultimate fallback');
            
            // Update location display
            this.setCurrentLocation(zipCode, 'zip', {
                latitude: 39.8283,
                longitude: -98.5795
            });
            
            // This is a manual request so pass false for isAutoRequest
            this.findNearestStore(39.8283, -98.5795, false); // Geographic center of the US
            
            // This is a manual request, so open the dropdown
            this.open = true;
            
            this.showLocationError("Could not find exact location for this ZIP code. Showing nearest stores based on approximate location.");
            
            // Notify the Store Locator component if it exists
            if (typeof userLocation !== 'undefined' && typeof loadStores === 'function') {
                userLocation = {
                    lat: 39.8283,
                    lng: -98.5795
                };
                loadStores({ isZipSearch: true });
            }
        }
    }

    calculateDistance(lat1, lon1, lat2, lon2) {
        // Use window.calculateDistance if available (from store-locator.js)
        if (typeof window.calculateDistance === 'function') {
            return window.calculateDistance(lat1, lon1, lat2, lon2);
        }
        
        // Haversine formula to calculate distance between two points
        const R = 3958.8; // Earth's radius in miles
        const dLat = this.toRadians(lat2 - lat1);
        const dLon = this.toRadians(lon2 - lon1);

        const a =
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
            Math.sin(dLon/2) * Math.sin(dLon/2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const distance = R * c;

        return distance;
    }
    
    toRadians(degrees) {
        return degrees * (Math.PI/180);
    }
    
    // Format distance with better precision
    formatDistance(distance) {
        if (distance === undefined || distance === null || isNaN(distance)) {
            return '';
        }
        
        if (distance < 0.1) {
            return distance.toFixed(2) + ' miles'; // Show 2 decimal places for very small distances
        } else if (distance < 1) {
            return distance.toFixed(1) + ' miles'; // Show 1 decimal place for distances under 1 mile
        } else {
            return Math.round(distance * 10) / 10 + ' miles'; // Round to nearest 0.1 for larger distances
        }
    }
    
    // Simple improved displayNearbyStores method
    displayNearbyStores(storesWithDistance) {
        // Get the existing stores list
        const storesList = this.querySelector('[data-stores-list]');
        if (!storesList) {
            console.error('Store list not found');
            return;
        }

        // Get all store items in the list
        const storeItems = storesList.querySelectorAll('li[data-store-id]');
        
        // If this is the first search, save the original order
        if (this.originalStoreOrder.length === 0) {
            this.originalStoreOrder = Array.from(storeItems).map(item => item.dataset.storeId);
        }
        
        // Simple solution: grab all li elements, add distances, and re-add them
        // Create a map of store items by ID for easy lookup
        const storeItemsMap = {};
        storeItems.forEach(item => {
            const storeId = item.dataset.storeId;
            // Create a clone to modify
            const clone = item.cloneNode(true);
            storeItemsMap[storeId] = clone;
        });
        
        // Create a map of distances by store ID
        const distanceMap = {};
        storesWithDistance.forEach(store => {
            distanceMap[store.id] = store.distance;
        });
        
        // Sort stores based on distance
        const sortedStoreIds = [...storesWithDistance]
            .sort((a, b) => a.distance - b.distance)
            .map(store => store.id);
        
        // Add remaining stores (those without distance info) at the end
        const remainingStoreIds = this.originalStoreOrder.filter(id => !sortedStoreIds.includes(id));
        
        // Combine sorted stores and remaining stores
        const allSortedIds = [...sortedStoreIds, ...remainingStoreIds];
        
        // Add distances to store elements
        Object.keys(storeItemsMap).forEach(storeId => {
            const distance = distanceMap[storeId];
            if (distance !== undefined) {
                const storeItem = storeItemsMap[storeId];
                const storeHeader = storeItem.querySelector('.store-picker__store-header');
                
                if (storeHeader) {
                    // Remove any existing distance indicator
                    const existingIndicator = storeHeader.querySelector('.store-distance-indicator');
                    if (existingIndicator) existingIndicator.remove();
                    
                    // Add distance indicator
                    const distanceIndicator = document.createElement('div');
                    distanceIndicator.className = 'store-distance-indicator';
                    distanceIndicator.style.cssText = 'font-size: 12px; color: #666; margin-top: 4px; text-align:right;';
                    distanceIndicator.textContent = this.formatDistance(distance);
                    storeHeader.appendChild(distanceIndicator);
                }
            }
        });
        
        // Clear the list and rebuild with correct dividers
        storesList.innerHTML = '';
        
        // Add all stores in the new order with dividers
        allSortedIds.forEach((storeId, index) => {
            const storeItem = storeItemsMap[storeId];
            if (storeItem) {
                storesList.appendChild(storeItem);
                
                // Add a divider after each store (except the last one)
                if (index < allSortedIds.length - 1) {
                    const divider = document.createElement('div');
                    divider.className = 'hr--small';
                    storesList.appendChild(divider);
                }
            }
        });
        
        // Update the location display
        this.updateLocationDisplay();
        
        // Move buttons below addresses
        this.moveButtonsBelowAddress();
    }
    
    // Simple method to restore original order
    resetStoreView() {
        const storesList = this.querySelector('[data-stores-list]');
        if (!storesList || this.originalStoreOrder.length === 0) return;
        
        // Clear saved location but don't update display yet (to avoid flicker)
        this.currentSearchLocation = '';
        
        // Get current store elements
        const currentStores = {};
        storesList.querySelectorAll('li[data-store-id]').forEach(item => {
            const clone = item.cloneNode(true);
            // Remove any distance indicators
            const indicator = clone.querySelector('.store-distance-indicator');
            if (indicator) indicator.remove();
            currentStores[item.dataset.storeId] = clone;
        });
        
        // Clear and rebuild in original order
        storesList.innerHTML = '';
        this.originalStoreOrder.forEach((storeId, index) => {
            const storeItem = currentStores[storeId];
            if (storeItem) {
                storesList.appendChild(storeItem);
                
                // Add a divider after each store (except the last one)
                if (index < this.originalStoreOrder.length - 1) {
                    const divider = document.createElement('div');
                    divider.className = 'hr--small';
                    storesList.appendChild(divider);
                }
            }
        });
        
        // Now update the display
        this.updateLocationDisplay();
        
        // Move buttons below addresses
        this.moveButtonsBelowAddress();
    }

    // Enhanced version of the showLocationError method
    showLocationError(message) {
        console.error('Location error:', message);

        // Find error element with more robust selector
        let errorElement = document.querySelector('.location-error');

        if (!errorElement) {
            console.error('Error container not found, creating one');
            const locationFinder = document.querySelector('.location-finder');
            
            if (locationFinder) {
                errorElement = document.createElement('div');
                errorElement.className = 'location-error';
                locationFinder.appendChild(errorElement);
            } else {
                console.error('Cannot find location-finder to append error element');
                return;
            }
        }

        errorElement.innerHTML = `
            <div class="error-message" style="background-color: #fff2f2; border-left: 3px solid #ff5252; color: #d32f2f; padding: 10px; display: flex; align-items: center; gap: 8px; font-size: 13px; border-radius: 3px; margin-top: 10px;">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                ${message}
            </div>
        `;

        // Auto-hide after 5 seconds
        setTimeout(() => {
            if (errorElement) {
                errorElement.innerHTML = '';
            }
        }, 5000);
    }

    // Diagnostic function
    diagnoseStorePicker() {
        console.group('Store Picker Diagnostics');
        console.log('Store Locations:', this.storeLocations.length);
        console.log('Original Store Order:', this.originalStoreOrder.length);
        console.log('Current Search Location:', this.currentSearchLocation);
        console.log('User Store:', this.userStore);
        console.log('localStorage:', {
            userStoreId: localStorage.getItem('userStoreId'),
            userStoreName: localStorage.getItem('userStoreName'),
            userSearchLocation: localStorage.getItem('userSearchLocation'),
            userSearchCoordinates: localStorage.getItem('userSearchCoordinates')
        });
        
        const storeElements = this.querySelectorAll('li[data-store-id]');
        console.log('Store Elements:', storeElements.length);
        console.groupEnd();
    }

    // handlers
    handleBodyClick(evt) {
        const target = evt.target;
        if (target.closest('store-picker summary')) {
            this.open = (this.open === true) ? false : true;
            return;
        }
        if (target.closest('store-picker button.store-fav-selector')) {
            let storeItem = target.closest('li');
            let favStoreId = storeItem.dataset.storeId;
            let favStoreName = storeItem.dataset.storeName;
            console.log('Store selected from picker:', favStoreId, favStoreName);
            this.userStore = favStoreId;
            return;
        }
        if (!target.closest('store-picker') && !target.closest('.location-finder')) {
            this.open = false;
            return;
        }
    }
    
    handleKeyup(evt) {
        if (evt.code.toUpperCase() === 'ESCAPE') this.open = false;
    }
    
    // getters
    get open() {
        return this.hasAttribute('open');
    }
    
    get userStore() {
        return this.getAttribute('user-store');
    }
    
    // setters
    set open(bool) {
        if (bool) {
            // Only open if this is not an initial load or auto request
            if (!this.isInitialLoad) {
                this.setAttribute('open', '');
            } else {
                console.log('Prevented opening dropdown during initial load');
            }
        } else {
            this.removeAttribute('open');
        }
    }
    
    set userStore(storeId) {
        this.setAttribute('user-store', storeId);
    }
    
    // methods
    hide() {
        setTimeout(() => {
            this.elements.details.removeAttribute('open');
        });
        if (this.open) this.open = false;
        document.body.removeEventListener('keyup', this.onKeyup);
        if (typeof removeTrapFocus === 'function') {
            removeTrapFocus(this.focusElement);
        }
    }
    
    show(focusElement) {
        this.focusElement = focusElement;
        
        // Only show if this is not an initial load
        if (!this.isInitialLoad) {
            setTimeout(() => {
                this.elements.details.setAttribute('open', '');
                // Move buttons below addresses when dropdown opens
                this.moveButtonsBelowAddress();
            });
        } else {
            console.log('Prevented showing dropdown during initial load');
        }
        
        if (!this.open && !this.isInitialLoad) this.open = true;
        document.body.addEventListener('keyup', this.onKeyup);
        if (typeof trapFocus === 'function') {
            trapFocus(this);
        }
    }
    
    assignStore() {
        if (!this.userStore) return;
        let favStore = this.querySelector(`li[data-store-id="${this.userStore}"]`);
        if (!favStore) {
            console.log('Store not found in picker:', this.userStore);
            return;
        }
        localStorage.setItem('userStoreId', this.userStore);
        if (favStore.dataset.storeName) {
            localStorage.setItem('userStoreName', favStore.dataset.storeName);
        }
        let favBtn = this.querySelector(`button[data-store-id="${this.userStore}"]`);
        if (favBtn) {
            favBtn.classList.remove('btn--secondary');
            favBtn.classList.add('store-fav-selector--enabled');
        }
        if (this.elements.selected) {
            this.elements.selected.innerText = favStore.dataset.storeName || "Your Store";
        }
        console.log('Store assigned in picker:', this.userStore, favStore.dataset.storeName);
        // Update store availability if it exists
        this.updateStoreAvailability();
    }
    
    updateStoreAvailability() {
        // Notify other components of the store change
        document.dispatchEvent(new CustomEvent('storeSelected', {
            detail: {
                storeId: this.userStore,
                storeName: localStorage.getItem('userStoreName')
            }
        }));
        console.log('Dispatched storeSelected event');
    }
    
    arrangeStores() {
        if (!this.userStore) return;
        const storesList = this.querySelector('[data-stores-list]');
        let favStore = this.querySelector(`[data-store-id="${this.userStore}"]`);
        if (storesList && favStore) {
            storesList.prepend(favStore);
            console.log('Arranged stores with selected store first');
            
            // Move buttons below addresses after arranging
            this.moveButtonsBelowAddress();
        }
    }
    
    clearStore() {
        this.querySelectorAll('.store-fav-selector').forEach(btn => {
            btn.classList.add('btn--secondary');
            btn.classList.remove('store-fav-selector--enabled');
        });
    }
    
    static get observedAttributes() {
        return ['open', 'user-store'];
    }
    
    attributeChangedCallback(name, oldValue, newValue) {
        console.log(`Attribute ${name} changed from ${oldValue} to ${newValue}`);
        switch (name) {
            case 'open':
                // Check if elements are initialized before proceeding
                if (!this.elements || !this.elements.summary) {
                    console.log('Elements not initialized yet, skipping open attribute handling');
                    return;
                }
                
                this.arrangeStores();
                if (newValue === null) {
                    this.hide();
                } else {
                    // Only show if not during initial load
                    if (!this.isInitialLoad) {
                        this.show(this.elements.summary);
                    } else {
                        console.log('Prevented opening dropdown during attribute change in initial load');
                    }
                }
                break;
            case 'user-store':
                this.clearStore();
                this.assignStore();
                // When store changes, ensure buttons are in correct position
                setTimeout(() => {
                    this.moveButtonsBelowAddress();
                }, 100);
                break;
        }
    }
}

// Make storeLocations array global if it exists in store-locator.js but isn't exported
document.addEventListener('DOMContentLoaded', () => {
    // Check if there's a storeLocations variable defined in any scope
    if (typeof storeLocations !== 'undefined' && !window.storeLocations) {
        window.storeLocations = storeLocations;
        console.log('Made storeLocations globally available');
    } else {
        console.log('Checking for window.storeLocations:', !!window.storeLocations);
    }

    // CSS for spinner animation
    const styleElement = document.createElement('style');
    styleElement.textContent = `
    @keyframes spinner {
      to {
        transform: rotate(360deg);
      }
    }
    `;
    document.head.appendChild(styleElement);
    
    // Run diagnostics
    setTimeout(() => {
        try {
            const storePicker = document.querySelector('store-picker');
            if (storePicker) {
                storePicker.diagnoseStorePicker();
            }
        } catch (e) {
            console.error('Error in store picker diagnostics:', e);
        }
    }, 2000);
});

// Listen for store selections from the availability drawer
document.addEventListener('storeSelected', (e) => {
    if (!e.detail || !e.detail.storeId) return;

    console.log('Store picker received storeSelected event:', e.detail);
    const storePicker = document.querySelector('store-picker');
    if (storePicker && storePicker.getAttribute('user-store') !== e.detail.storeId) {
        storePicker.setAttribute('user-store', e.detail.storeId);
    }
});

// Add global calculateDistance if missing
if (typeof window.calculateDistance !== 'function') {
    window.calculateDistance = function(lat1, lon1, lat2, lon2) {
        // Validate inputs
        if (typeof lat1 !== 'number' || typeof lon1 !== 'number' || 
            typeof lat2 !== 'number' || typeof lon2 !== 'number' ||
            isNaN(lat1) || isNaN(lon1) || isNaN(lat2) || isNaN(lon2)) {
            console.warn('Invalid coordinates in distance calculation:', { lat1, lon1, lat2, lon2 });
            return Infinity;
        }
        
        const R = 3958.8; // Earth radius in miles
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
            Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const distance = R * c;
        
        return distance;
    };
}

// Use the location info from store locator if available
window.addEventListener('load', () => {
    setTimeout(() => {
        if (typeof userLocation !== 'undefined' && userLocation) {
            console.log('Using userLocation from store-locator.js', userLocation);
            localStorage.setItem('userSearchCoordinates', JSON.stringify({
                latitude: userLocation.lat,
                longitude: userLocation.lng
            }));
            localStorage.setItem('userSearchLocationType', 'geo');
            localStorage.setItem('userSearchLocation', 'your current location');
        }
    }, 1500);
});

customElements.define('store-picker', StorePicker);